/**
 * Every path the dashboard calls, and the one function that builds a query string.
 *
 * Paths are built here rather than interpolated at each call site for the reason
 * `src/lib/scope.ts` gives about predicates: the value that decides *what* is being
 * asked for should be written once, where it can be read, rather than assembled
 * inside a component beside the code that renders the answer.
 *
 * ── The one rule about `employee_id` ────────────────────────────────────────
 *
 * **A self-view never sends it.** `/my-activity` and `/time-tracking` (WP-K2) are
 * self-views: step 5 of the 02 §11 chain resolves the caller to themselves, so an
 * omitted `employee_id` is exactly the self-scope, and sending one would be the
 * client asserting a scope rather than accepting the one the server resolved.
 * `openapi.yaml` says the parameter "never widens scope", which is true of the
 * server; it is the *client* naming a subject at all that a self-view declines to
 * do. `qs` drops `undefined` values rather than serialising them, so an absent id is
 * an absent parameter and not `employee_id=undefined`.
 *
 * **`/attendance` (WP-K3) is not a self-view, and it does send one.** It is the
 * screen where somebody looks at *other people*, and naming the subject is the whole
 * request — "show me this employee's month" is not expressible without it. That is
 * still not an assertion of scope: the server intersects the filter with whatever
 * `resolveScope` resolved, so an id outside the caller's field of view narrows the
 * answer to nothing or is refused, and never widens it. The rule is therefore not
 * "the client must not name a subject"; it is **"a self-view must not, because it
 * has no subject to name"**.
 */

import { addDays, startOfLocalDay } from '../i18n/datetime';

/** Query values a caller may pass. `undefined` means "do not send this parameter". */
export type QueryValue = string | number | boolean | undefined;

/**
 * `?a=1&b=2`, or `''` when nothing survives.
 *
 * `URLSearchParams` does the escaping, so a search term containing `&`, `#` or a
 * Vietnamese diacritic is encoded rather than splicing itself into the query. It is
 * also why the empty case returns `''` and not `'?'`: a bare `?` is a different URL
 * from no query at all, and the fetch stub's route map would miss it.
 */
export function qs(params: Readonly<Record<string, QueryValue>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered === '' ? '' : `?${rendered}`;
}

export const ATTENDANCE_PATH = '/api/v1/attendance';
export const ATTENDANCE_CORRECTIONS_PATH = '/api/v1/attendance/corrections';
export const ACTIVITY_TIMELINE_PATH = '/api/v1/activities/timeline';
export const ACTIVITY_CATALOG_PATH = '/api/v1/activities/catalog';
export const TIME_ENTRIES_PATH = '/api/v1/time-entries';
export const MANUAL_TIME_ENTRY_PATH = '/api/v1/time-entries/manual';
export const TIMERS_PATH = '/api/v1/timers';
export const TASK_SEARCH_PATH = '/api/v1/tasks/search';
export const PRODUCTIVITY_EXPLAIN_PATH = '/api/v1/productivity/explain';

/** One local date's attendance. `date_from = date_to` — the day, not a range. */
export function attendanceDayPath(date: string): string {
  return `${ATTENDANCE_PATH}${qs({ date_from: date, date_to: date, limit: 2 })}`;
}

/**
 * The hour rollup for one instant window.
 *
 * `granularity=hour` and `source=aggregate` are written out rather than left to the
 * defaults. They are the two parameters that decide which table answers, and a
 * screen whose meaning depends on a default it does not state is a screen that
 * changes meaning when the default does.
 */
export function timelineHoursPath(fromIso: string, toIso: string): string {
  return `${ACTIVITY_TIMELINE_PATH}${qs({
    from: fromIso,
    to: toIso,
    source: 'aggregate',
    granularity: 'hour',
    limit: 100,
  })}`;
}

export function catalogPath(onDate: string): string {
  return `${ACTIVITY_CATALOG_PATH}${qs({ on_date: onDate })}`;
}

export function explainPath(employeeId: string, from: string, to: string): string {
  return `${PRODUCTIVITY_EXPLAIN_PATH}/${encodeURIComponent(employeeId)}${qs({
    date_from: from,
    date_to: to,
  })}`;
}

export function timeEntriesPath(params: Readonly<Record<string, QueryValue>>): string {
  return `${TIME_ENTRIES_PATH}${qs(params)}`;
}

export function taskSearchPath(query: string, limit = 10): string {
  return `${TASK_SEARCH_PATH}${qs({ q: query, limit })}`;
}

export function timerActionPath(id: string, action: 'pause' | 'resume' | 'stop'): string {
  return `${TIMERS_PATH}/${encodeURIComponent(id)}/${action}`;
}

export const TIMER_START_PATH = `${TIMERS_PATH}/start`;

/* ══════════════════ WP-K3 (Task 43) — the three requests /attendance makes ══ */

/**
 * The grid read: `GET /attendance` over a window, optionally narrowed.
 *
 * `limit` is **explicit at every call site** rather than left at the endpoint's
 * default of 50, because the page boundary here is not a display detail: the grid is
 * `(employee × date)` and paged **employee-major** (`offset / range.days` in
 * `attendance-service.ts`), so a 31-day window at limit 50 is one employee and
 * nineteen days of a second one. A screen that did not choose its own limit would be
 * showing a page boundary in the middle of somebody's month and calling it data.
 *
 * `department_id` and `employee_id` are **filters, not scope**. The server
 * intersects them with `WpScope.employeeIds`; see this file's header.
 */
export function attendanceGridPath(query: {
  readonly from: string;
  readonly to: string;
  readonly limit: number;
  readonly offset?: number;
  readonly employeeId?: string | undefined;
  readonly departmentId?: string | undefined;
}): string {
  return `${ATTENDANCE_PATH}${qs({
    date_from: query.from,
    date_to: query.to,
    limit: query.limit,
    offset: query.offset,
    employee_id: query.employeeId,
    department_id: query.departmentId,
  })}`;
}

/**
 * The drawer read: `GET /attendance/{employeeId}/{date}`.
 *
 * A separate request from the grid rather than a lookup in the page already in
 * memory, and deliberately: this is the endpoint that carries the provenance block
 * (`shift.source`) the drawer exists to show, and re-reading it means the drawer
 * answers for the row as it is **now** rather than as it was when the month was
 * fetched. A day recomputed between the two is the case that matters.
 */
export function attendanceDetailPath(employeeId: string, date: string): string {
  return `${ATTENDANCE_PATH}/${encodeURIComponent(employeeId)}/${encodeURIComponent(date)}`;
}

/**
 * `POST /attendance/corrections/{id}/approve` and `/reject`.
 *
 * The two mounted decision endpoints. There is **no `GET`** on this collection
 * anywhere in `openapi.yaml` — not an unmounted one, none at all — which is why
 * `web/shapes/attendance.md` §9 makes the queue a decision surface keyed by id
 * rather than a list.
 */
export function correctionDecisionPath(id: string, decision: 'approve' | 'reject'): string {
  return `${ATTENDANCE_CORRECTIONS_PATH}/${encodeURIComponent(id)}/${decision}`;
}

/* ══════════════════ WP-K4 (Task 44) — the four reads /overview makes ════════ */

export const DEVICES_PATH = '/api/v1/devices';

/**
 * The day rollup for a date window, over whatever scope the server resolved.
 *
 * `granularity=day` and `source=aggregate` are written out rather than left to the
 * defaults, for the reason `timelineHoursPath` gives: a screen whose meaning depends
 * on a default it does not state changes meaning when the default does.
 *
 * **No `employee_id`.** This is not a self-view — it is the aggregate over the
 * caller's resolved scope, and naming a subject would narrow it to one person.
 *
 * `limit` is the endpoint's maximum, and it is explicit because `web/shapes/
 * overview.md` §8 turns the page boundary into a *narrowed window* rather than a
 * partial sum: the rows are ordered `session_date, employee_id`, so every date
 * strictly before the last one on the page is fully represented and the totals say
 * which dates they used.
 *
 * ── The window is half-open, and its ends are the COMPANY's midnights ────────
 *
 * `from` and `to` are instants, not dates, and `to` is **exclusive** — that is what
 * `openapi.yaml`'s `From`/`To` parameters say and what `read-service.ts` implements
 * (`localDateAt(timezone, toMs - 1)` is the last date it reads; `to <= from` is
 * refused outright as `400 … "to must be after from"`). So this takes the window as
 * the two **local dates** a screen actually filters on, inclusive, plus the zone they
 * are dates in, and converts.
 *
 * It is written this way rather than left to the caller because both halves were got
 * wrong at once (QA-G-009): passing the two dates through meant the default one-day
 * window asked for `from == to` and was refused every time, and it meant the window
 * was cut on UTC's midnight rather than the company's — which for a tenant at or west
 * of Greenwich silently dropped the last day of every window. Requiring `zone` here
 * makes "whose day is this?" a question no caller can forget to answer.
 */
export const TIMELINE_PAGE_LIMIT = 500;

export function dailyAggregatePath(from: string, toInclusive: string, zone: string): string {
  return `${ACTIVITY_TIMELINE_PATH}${qs({
    from: startOfLocalDay(zone, from),
    to: startOfLocalDay(zone, addDays(toInclusive, 1)),
    source: 'aggregate',
    granularity: 'day',
    limit: TIMELINE_PAGE_LIMIT,
  })}`;
}

/**
 * The roster read: today's `(employee × date)` column.
 *
 * One date, so `total` **is** the in-scope headcount — the endpoint pages the grid
 * employee-major and emits a cell for every employee whether or not a session row
 * exists (Task 26 writes no row for an employee with no events at all, which is
 * exactly the person a dashboard must not lose). `limit` is explicit for the reason
 * `attendanceGridPath` gives.
 */
export function overviewRosterPath(
  date: string,
  departmentId: string | undefined,
  limit: number,
): string {
  return `${ATTENDANCE_PATH}${qs({
    date_from: date,
    date_to: date,
    limit,
    department_id: departmentId,
  })}`;
}

/**
 * Every timer running right now, in the caller's resolved scope.
 *
 * `status=running` and nothing else: `paused` is not running, and a paused entry
 * listed as somebody's current task would put a manager in front of a person who
 * stopped an hour ago.
 */
export function runningTimersPath(limit: number): string {
  return `${TIME_ENTRIES_PATH}${qs({ status: 'running', limit })}`;
}

/**
 * The device liveness strip, for a session holding `workforce.manage_devices`.
 *
 * No `online` filter: the whole point of the strip is that `online`, `stale` and
 * `offline` are three different answers, and `online=false` would fold the last two
 * together in the request itself.
 */
export function devicesPath(limit: number, departmentId: string | undefined): string {
  return `${DEVICES_PATH}${qs({ limit, department_id: departmentId })}`;
}

/**
 * The drill-down target, with the overview's filter context already in it.
 *
 * Spec 01 §12: "Drill-down giữ nguyên filter context." The context lives in the
 * overview's own query string, so this is a projection of it rather than a second
 * copy — see `web/shapes/overview.md` §7. `AttendanceScreen` seeds its date,
 * department and subject from exactly these three names.
 */
export function attendanceDrillDownPath(context: {
  readonly date: string;
  readonly departmentId?: string | undefined;
  readonly employeeId?: string | undefined;
}): string {
  return `/attendance${qs({
    date: context.date,
    department_id: context.departmentId,
    employee_id: context.employeeId,
  })}`;
}

/**
 * The task-cache health probe.
 *
 * Spec 01 §12 makes "task data delayed khi achau down" an acceptance criterion, and
 * the only publisher of that fact is `TaskSearchResponse` — `degraded`,
 * `cache_status` and the rows' `cached_at`. There is **no** endpoint that reports
 * the sweep's state on its own: `POST /admin/sync` returns a `last_sync` block and
 * is a `POST` behind `workforce.manage_policies`, so a dashboard cannot read it.
 *
 * So this is a search issued for its **envelope**, and the screen says so on the
 * tile rather than letting a reader assume the numbers came from somewhere tidier.
 * The result rows are not rendered anywhere. `cache_status` is the company's sweep
 * state and does not depend on `q` at all; `degraded` is a property of this request;
 * only `cached_at` depends on which rows matched, which is why the copy says "the
 * newest record this probe saw" rather than "the last sweep".
 *
 * A single letter is the shortest query `openapi.yaml` accepts (`minLength: 1`) and
 * the widest net a substring match can cast. When an endpoint lands that answers the
 * sweep state directly, this function and the sentence beside it both go.
 */
export const TASK_CACHE_PROBE_QUERY = 'a';
export const TASK_CACHE_PROBE_LIMIT = 20;

export function taskCacheProbePath(): string {
  return `${TASK_SEARCH_PATH}${qs({
    q: TASK_CACHE_PROBE_QUERY,
    limit: TASK_CACHE_PROBE_LIMIT,
  })}`;
}

/* ══════════════════ WP-K5 (Task 45) — the devices and policies screens ══════ */

export const REGISTRATION_TOKENS_PATH = `${DEVICES_PATH}/registration-tokens`;
export const ADMIN_WEB_CATALOG_PATH = '/api/v1/admin/activity-catalog/websites';
export const ADMIN_CATALOG_CATEGORIES_PATH = '/api/v1/admin/activity-catalog/categories';

/**
 * The fleet page.
 *
 * **`online` is never sent.** The whole point of the list is that `online`, `stale`
 * and `offline` are three answers and `never reported` is a fourth; `online=false`
 * would fold the last three together inside the request itself, before the screen
 * ever got a chance not to. `status` is a different axis and *is* sent, because it
 * is a lifecycle filter and the server's own enum.
 *
 * Every other narrowing here is a **server** parameter rather than a filter applied
 * to the page after it arrives. A client-side filter over one page of an offset-paged
 * endpoint silently answers "…among these fifty", which is not the question anybody
 * typed, and the count beside it would be wrong in a way nobody can see.
 *
 * `q` matches `hostname` only — `wp_devices` stores just a `fingerprint_hash` and no
 * response returns it — and the screen's hint says so, so nobody pastes a
 * fingerprint in and concludes the device is gone.
 */
export function devicesAdminPath(query: {
  readonly limit: number;
  readonly offset?: number;
  readonly status?: string | undefined;
  readonly q?: string | undefined;
  readonly employeeId?: string | undefined;
  readonly departmentId?: string | undefined;
}): string {
  return `${DEVICES_PATH}${qs({
    limit: query.limit,
    offset: query.offset,
    status: query.status,
    q: query.q,
    employee_id: query.employeeId,
    department_id: query.departmentId,
  })}`;
}

export function devicePath(deviceId: string): string {
  return `${DEVICES_PATH}/${encodeURIComponent(deviceId)}`;
}

export function deviceHealthPath(deviceId: string): string {
  return `${devicePath(deviceId)}/health`;
}

/**
 * `POST /devices/{id}/approve` and `/revoke`.
 *
 * Neither takes an `Idempotency-Key`, and that is the server's decision rather than
 * an omission here: "the transition table already makes a repeat a `409` rather than
 * a second approval, so the header would promise a replay semantics this endpoint
 * does not implement". A revoked device revoked twice is a `409` with an **empty**
 * `allowed_transitions[]`, which is how the first revocation's actor, timestamp and
 * reason survive.
 */
export function deviceTransitionPath(deviceId: string, action: 'approve' | 'revoke'): string {
  return `${devicePath(deviceId)}/${action}`;
}

export function registrationTokensPath(limit: number, offset = 0): string {
  return `${REGISTRATION_TOKENS_PATH}${qs({ limit, offset })}`;
}

export function registrationTokenRevokePath(tokenId: string): string {
  return `${REGISTRATION_TOKENS_PATH}/${encodeURIComponent(tokenId)}/revoke`;
}

/**
 * The website catalogue — the one part of a company's surveillance posture that has
 * a session endpoint at all.
 *
 * **`on_date` is deliberately not sent.** It would read the catalogue as it stood on
 * some other day, and this screen edits the rules in force *now*; showing a
 * historical reading beside a save button that writes to today would be two
 * different questions in one panel.
 */
export function websiteCatalogPath(limit: number, offset = 0): string {
  return `${ADMIN_WEB_CATALOG_PATH}${qs({ limit, offset })}`;
}

/* ══════════════════ WP-L5 (Task 53) — alerts, incidents and data risk ═══════
 *
 * Every narrowing below is a **server** parameter. A client-side filter over one
 * page of a paged endpoint silently answers "…among these fifty", which is not the
 * question anybody typed, and the count printed beside it would be wrong in a way
 * nobody can see. The one place this build cannot honour that rule is the Data Risk
 * page's blocked-domain table — `GET /activities/websites` publishes no
 * `blacklisted` filter — and that table says on screen exactly what it counted.
 *
 * **`X-Access-Reason` is not here**, and that is deliberate rather than an omission:
 * it is a request *header*, mandatory in v1 on every one of these reads, and putting
 * it in a path builder would be the one place a reader would not look for it. It is
 * passed through `useApiQuery`'s `headers` option, from one field on each screen.
 */

export const ALERTS_PATH = '/api/v1/alerts';
export const INCIDENTS_PATH = '/api/v1/incidents';
export const ACTIVITY_WEBSITES_PATH = '/api/v1/activities/websites';

/**
 * The alert inbox page.
 *
 * `severity`, `status` and `trigger` are **single-valued on the server** — the router
 * refuses a repeated parameter with `must be a single value` — so the filter is
 * single-select. Offering checkboxes and OR-ing them in the browser would be the
 * client-side filter this file's header rejects, over a **cursor** page at that,
 * where there is not even a total to notice the error against.
 *
 * `cursor` is the previous page's `page.next_cursor`, opaque and forwarded unread.
 */
export function alertsPath(query: {
  readonly limit: number;
  readonly severity?: string | undefined;
  readonly status?: string | undefined;
  readonly trigger?: string | undefined;
  readonly cursor?: string | undefined;
}): string {
  return `${ALERTS_PATH}${qs({
    limit: query.limit,
    severity: query.severity,
    status: query.status,
    trigger: query.trigger,
    cursor: query.cursor,
  })}`;
}

/**
 * `POST /alerts/{id}/acknowledge`.
 *
 * Safe to press twice: repeating it is a `200` no-op returning the **original**
 * `acknowledged_at` and `acknowledged_by`, because `wp_alert_events_guard()` refuses
 * every UPDATE to a row that already carries one. So there is no `Idempotency-Key`
 * here and none is wanted — the immutability is the idempotence.
 */
export function alertAcknowledgePath(alertId: string): string {
  return `${ALERTS_PATH}/${encodeURIComponent(alertId)}/acknowledge`;
}

/**
 * The incident queue page. Offset-paged, so `page.total` is a real total.
 *
 * `state`, `severity` and `owner_id` are the server's own filters. There is **no
 * `category`** parameter and no category on the wire at all, which is why the Data
 * Risk page cannot narrow incidents to data-risk ones and says so instead.
 */
export function incidentsPath(query: {
  readonly limit: number;
  readonly offset?: number;
  readonly state?: string | undefined;
  readonly severity?: string | undefined;
  readonly ownerId?: string | undefined;
}): string {
  return `${INCIDENTS_PATH}${qs({
    limit: query.limit,
    offset: query.offset,
    state: query.state,
    severity: query.severity,
    owner_id: query.ownerId,
  })}`;
}

/** `GET`/`PATCH /incidents/{id}` — the detail representation, comments included. */
export function incidentPath(incidentId: string): string {
  return `${INCIDENTS_PATH}/${encodeURIComponent(incidentId)}`;
}

/**
 * `POST /incidents/{id}/transition` — the single door `state` moves through.
 *
 * **No `If-Match`**, and that is the server's decision rather than an omission here:
 * `updated_at` is read under `FOR UPDATE` inside the transaction and the UPDATE names
 * it, so two operators transitioning at once produce one move and one `409` without a
 * precondition header. A header would additionally forbid a transition from a stale
 * screen, which is exactly the case where an incident queue most wants the decision to
 * land. `PATCH` is the operation that takes one.
 */
export function incidentTransitionPath(incidentId: string): string {
  return `${incidentPath(incidentId)}/transition`;
}

/**
 * The blocked-domain read behind the Data Risk page.
 *
 * There is **no `blacklisted` query parameter** — the flag is on the row and the
 * endpoint does not filter on it — so this reads one page and the screen filters it,
 * against this file's own rule, and prints what it counted out of `total` rather than
 * a number that would quietly mean "…among the first two hundred". Closing that
 * properly needs a server-side filter, which is a finding, not a client fix.
 */
export function websiteUsagePath(query: {
  readonly from: string;
  readonly to: string;
  readonly limit: number;
}): string {
  return `${ACTIVITY_WEBSITES_PATH}${qs({
    date_from: query.from,
    date_to: query.to,
    limit: query.limit,
  })}`;
}

/* ══════════════════ WP-K8 (Task 48) — reports, schedules and the audit log ══
 *
 * Every narrowing below is a **server** parameter, for this file's own reason: a
 * client-side filter over a paged endpoint answers "…among these fifty", which is
 * not the question anybody typed. That rule matters most on the audit log, where the
 * pagination is a **cursor** and there is not even a total to notice the error
 * against — an investigator who narrows to one actor and gets three rows has to be
 * able to trust that three is the answer to their question.
 */

export const REPORTS_PATH = '/api/v1/reports';
export const REPORT_SCHEDULES_PATH = '/api/v1/report-schedules';
export const AUDIT_LOGS_PATH = '/api/v1/audit-logs';

/**
 * `GET /reports` — the company's export queue (G26, amendment A20).
 *
 * **Offset, with a real `total`.** `API_CONTRACT.md` §1.6 reserves offset for a
 * small stable catalogue whose screen is a work queue and needs a count — "eleven
 * asked for, two failed" — and `wp_reports` is one: retention sweeps the row 90 days
 * past `expires_at`, so the ceiling is known.
 *
 * `limit` is written at the call site rather than left to the endpoint's default of
 * 50, the rule this file already applies to `/attendance`: a page boundary is a fact
 * about the request, and a screen that did not choose its own is showing somebody
 * else's.
 *
 * `status` and `report_type` are **server** parameters. The endpoint validates each
 * against the column's own vocabulary and answers `400` on an unknown one, which is
 * the behaviour a filter wants here: a silent empty page on this screen reads as
 * "this company has never exported anything".
 */
export function reportsListPath(query: {
  readonly limit: number;
  readonly offset?: number;
  readonly status?: string | undefined;
  readonly reportType?: string | undefined;
}): string {
  return `${REPORTS_PATH}${qs({
    limit: query.limit,
    offset: query.offset,
    status: query.status,
    report_type: query.reportType,
  })}`;
}

/**
 * `GET /reports/{id}` — the status read one row polls itself with.
 *
 * Until G26 this was the **only** way to reach a report, and the screen's list was
 * "what this browser started, in this tab" because of it. `reportsListPath` above is
 * that gap closed; this stays because a row that is still generating re-reads itself
 * rather than re-reading the whole queue every five seconds.
 */
export function reportPath(reportId: string): string {
  return `${REPORTS_PATH}/${encodeURIComponent(reportId)}`;
}

/**
 * `GET /reports/{id}/download?mode=json`.
 *
 * **`mode=json`, not the default `302`**, and it is this client that cannot follow
 * the redirect rather than the endpoint that is odd: `api/client.ts` sets
 * `redirect: 'error'` because following one to the object store would hand a JSON
 * parser an S3 body and would drop the session cookie crossing hosts. So the URL
 * comes back in the body and the page performs a **navigation**, which is also what
 * makes `Content-Disposition` work.
 *
 * The response is a bearer credential with a 180-second life (SEC-48). It is handled
 * and never stored — see `web/src/reports/download.ts`.
 */
export function reportDownloadPath(reportId: string): string {
  return `${reportPath(reportId)}/download${qs({ mode: 'json' })}`;
}

/**
 * `GET`, `PATCH` and `DELETE /report-schedules/{id}` — one schedule, by an id the
 * caller already holds.
 *
 * **There is still no `GET /report-schedules`.** `src/modules/schedules/routes.ts`
 * mounts a create on the collection and three operations on the item, and nothing
 * that enumerates. So this function is reachable only from a create's `201` body or
 * from an id somebody wrote down, and that remaining gap is what `AbsentEndpoints`
 * still names.
 *
 * The three share one path because they are one resource: `GET` mints the `ETag`
 * that `PATCH` must send back as `If-Match`, and `DELETE` takes no precondition at
 * all — the server's own reasoning, on the route: the question a delete asks is
 * "does this exist", and its `(company_id, id)` predicate answers it atomically.
 */
export function reportSchedulePath(scheduleId: string): string {
  return `${REPORT_SCHEDULES_PATH}/${encodeURIComponent(scheduleId)}`;
}

/**
 * `GET /audit-logs` — the one read behind `/audit-log`.
 *
 * All nine narrowings are **server** parameters, declared in `openapi.yaml`:
 * `log_type`, `actor_id`, `action`, `resource_type`, `resource_id`, `from`, `to`,
 * `success` and `cursor`. Not one of them is applied in the browser, and on this
 * endpoint that rule is stricter than usual rather than merely tidy — the envelope
 * is `CursorListEnvelope`, which publishes `next_cursor` and `has_more` and **no
 * total**, so a browser-side filter would leave a count with nothing to check it
 * against.
 *
 * `action` is an **exact match on the verb** (`maxLength: 128`), not a substring
 * search. The field says so, because typing `device` and getting nothing back would
 * otherwise read as "no device actions were audited".
 *
 * `cursor` is the previous page's `page.next_cursor`, opaque and forwarded unread.
 *
 * **`X-Access-Reason` is not here**, deliberately, and for the reason the WP-L5 block
 * above gives: it is a request *header*, mandatory on this sensitive read, and a path
 * builder is the one place a reader would not look for it. It travels through
 * `useApiQuery`'s `headers` option from the one field on the screen.
 */
export function auditLogsPath(query: {
  readonly limit: number;
  readonly logType?: string | undefined;
  readonly actorId?: string | undefined;
  readonly action?: string | undefined;
  readonly resourceType?: string | undefined;
  readonly resourceId?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly success?: boolean | undefined;
  readonly cursor?: string | undefined;
}): string {
  return `${AUDIT_LOGS_PATH}${qs({
    limit: query.limit,
    log_type: query.logType,
    actor_id: query.actorId,
    action: query.action,
    resource_type: query.resourceType,
    resource_id: query.resourceId,
    from: query.from,
    to: query.to,
    success: query.success,
    cursor: query.cursor,
  })}`;
}

/* ══════════════════ WP-M4 (Task 57) — the four AI reads and writes ══════════
 *
 * Every narrowing below is a **server** parameter, for this file's own reason. It
 * matters more here than on most screens: `GET /ai/insights` is **cursor**-paged and
 * `CursorListEnvelope` publishes `next_cursor` and `has_more` and **no total**, so a
 * browser-side filter would leave a count with nothing to check it against — and the
 * count in question is "how many analyses were written about your people".
 *
 * **`X-Access-Reason` is not here**, deliberately and for the reason the WP-L5 block
 * gives: it is a request *header*, mandatory on `GET /ai/insights/{id}/evidence`
 * (`x-wp-sensitive-access: true`), and a path builder is the one place a reader would
 * not look for it. It travels through `useApiQuery`'s `headers` option from the one
 * field on the screen. The other three AI operations are **not** sensitive-access and
 * deliberately send no reason at all: collecting one the server neither requires nor
 * stores is collecting it under false pretences (Task 48 §9.6).
 */

export const AI_INSIGHTS_PATH = '/api/v1/ai/insights';
export const AI_ANALYZE_PATH = '/api/v1/ai/analyze';
export const AI_CHAT_PATH = '/api/v1/ai/chat';

/**
 * The insight page.
 *
 * `insight_type`, `date_from`, `date_to`, `employee_id`, `job_id`, `cursor` and
 * `limit` are all declared parameters. `cursor` is the previous page's
 * `page.next_cursor`, opaque and forwarded unread.
 */
export function aiInsightsPath(query: {
  readonly limit: number;
  readonly insightType?: string | undefined;
  readonly dateFrom?: string | undefined;
  readonly dateTo?: string | undefined;
  readonly employeeId?: string | undefined;
  readonly jobId?: string | undefined;
  readonly cursor?: string | undefined;
}): string {
  return `${AI_INSIGHTS_PATH}${qs({
    limit: query.limit,
    insight_type: query.insightType,
    date_from: query.dateFrom,
    date_to: query.dateTo,
    employee_id: query.employeeId,
    job_id: query.jobId,
    cursor: query.cursor,
  })}`;
}

/**
 * The poll for `POST /ai/analyze`.
 *
 * §8.9 publishes **no job-status endpoint** — this list is the whole of it. An empty
 * page means the worker has not written its row yet; a page of one means it has,
 * **whatever that row says**: a `fallback` carrying a diagnosis is an answer, and a
 * `failed` row is a budget refusal, and neither is an error to this screen.
 *
 * What it cannot say is *why* a page is still empty — queued, running, or a worker
 * that died — and the screen says that out loud rather than spinning for ever.
 */
export function aiInsightByJobPath(jobId: string): string {
  return aiInsightsPath({ limit: 1, jobId });
}

/**
 * `GET /ai/insights/{id}/evidence` — the one **sensitive** read on this screen.
 *
 * It writes a `wp_data_access_logs` row with `access_type: 'ai_query'`,
 * `record_count` equal to the rows actually handed over and `target_employee_ids`
 * equal to the people actually read (SEC-35). Missing reason → `403
 * WORKFORCE_ACCESS_REASON_REQUIRED`, and **no log row is written**.
 */
export function aiEvidencePath(insightId: string): string {
  return `${AI_INSIGHTS_PATH}/${encodeURIComponent(insightId)}/evidence`;
}
